(() => {
  if (customElements.get('vn-cart-enhancement')) return;
  customElements.define('vn-cart-enhancement', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.schedule = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refresh(), 100);
      };
      document.addEventListener('cart:update', this.schedule, {signal:this.controller.signal});
      document.addEventListener('discount:update', this.schedule, {signal:this.controller.signal});
      this.addEventListener('click', e => {
        const button = e.target.closest('[data-vnc-add]');
        if (button && this.contains(button)) this.add(button);
      }, {signal:this.controller.signal});
      this.observer = new MutationObserver(() => { this.titles(); this.layout(); });
      document.querySelectorAll('cart-items-component').forEach(node => this.observer.observe(node, {childList:true, subtree:true}));
      this.titles();
      this.layout();
      this.schedule();
    }
    layout() {
      const drawer = this.closest('.cart-drawer__dialog');
      const items = drawer?.querySelector('.cart-drawer__items');
      if (!items) return;
      if (this.list !== items) {
        this.list = items;
        let button = drawer.querySelector('[data-vnc-more]');
        if (!button) {
          button = document.createElement('button');
          button.type = 'button';
          button.className = 'vnc__more';
          button.dataset.vncMore = '';
          const language = (document.documentElement.lang || 'en').split('-')[0];
          const label = {da:'Se flere varer',de:'Weitere Artikel anzeigen',fr:'Voir les autres articles'}[language] || 'See more items';
          button.append(document.createTextNode(label));
          const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
          svg.setAttribute('width','16'); svg.setAttribute('height','16'); svg.setAttribute('viewBox','0 0 24 24'); svg.setAttribute('aria-hidden','true');
          svg.innerHTML = '<path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.8"/>';
          button.append(svg);
          items.after(button);
        }
        this.moreButton = button;
        this.checkOverflow = () => button.hidden = items.scrollHeight - items.clientHeight - items.scrollTop < 8;
        button.addEventListener('click', () => items.scrollBy({top:Math.max(100,items.clientHeight * .7),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto':'smooth'}), {signal:this.controller.signal});
        items.addEventListener('scroll', this.checkOverflow, {passive:true,signal:this.controller.signal});
        this.resizeObserver?.disconnect();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.checkOverflow);
          this.resizeObserver.observe(items);
        }
      }
      this.checkOverflow();
    }
    config() {
      try { return JSON.parse(this.querySelector('[data-vnc-config]').textContent); }
      catch { return null; }
    }
    titles() {
      const config = this.config();
      if (!config) return;
      document.querySelectorAll('cart-items-component .cart-items__title').forEach(link => {
        const url = new URL(link.href, location.origin);
        const variant = url.searchParams.get('variant');
        const item = config.titles.find(item => String(item.variant) === variant)
          || config.titles.find(item => url.pathname.endsWith(item.path));
        if (item && link.textContent.trim() !== item.title) link.textContent = item.title;
      });
    }
    async refresh() {
      this.titles();
      this.layout();
      this.request?.abort();
      this.request = new AbortController();
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {cache:'no-store', headers:{Accept:'application/json'}, signal:this.request.signal});
        if (!response.ok) return;
        const cart = await response.json();
        if (this.isConnected) this.paint(cart);
      } catch (error) { if (error.name !== 'AbortError') console.warn('Cart delivery status could not be updated.'); }
    }
    paint(cart) {
      const c = this.config();
      if (!c) return;
      const physical = cart.items.some(item => item.requires_shipping);
      const shipping = this.querySelector('[data-vnc-shipping]');
      shipping.hidden = !physical || !c.threshold;
      const goal = cart.currency === c.currency ? c.goal : 0;
      const remaining = Math.max(0, goal - cart.total_price);
      const track = this.querySelector('[data-vnc-track]');
      track.hidden = !goal;
      const progress = goal ? Math.min(100, Math.max(0, cart.total_price / goal * 100)) : 0;
      track.setAttribute('aria-valuenow', Math.round(progress));
      this.querySelector('[data-vnc-fill]').style.width = `${progress}%`;
      const message = this.querySelector('[data-vnc-message]');
      let text = c.country === 'DK' ? c.labels.dk : goal && !remaining ? c.labels.unlocked : goal ? c.labels.remaining : c.labels.fallback;
      let amount = c.threshold;
      if (goal) amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:cart.currency}).format(remaining / 100);
      const parts = text.split('[amount]');
      message.replaceChildren(document.createTextNode(parts[0]));
      if (parts.length > 1) {
        const strong = document.createElement('strong');
        strong.textContent = amount;
        message.append(strong, document.createTextNode(parts.slice(1).join('[amount]')));
      }
      const customs = this.querySelector('[data-vnc-customs]');
      if (customs) customs.hidden = !physical;
      const offers = [...this.querySelectorAll('[data-offer-product]')];
      offers.forEach(offer => offer.hidden = cart.items.some(item => String(item.product_id) === offer.dataset.offerProduct));
      this.querySelector('[data-vnc-extras]').hidden = !physical || !goal || !remaining || !offers.some(offer => !offer.hidden);
    }
    async add(button) {
      if (button.disabled) return;
      const c = this.config();
      const error = this.querySelector('[data-vnc-error]');
      error.textContent = '';
      button.disabled = true;
      const body = new FormData();
      body.set('id', button.dataset.vncAdd);
      body.set('quantity', '1');
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))].filter(Boolean).slice(0,5);
      if (sections.length) body.set('sections', sections.join(','));
      body.set('sections_url', location.pathname);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {method:'POST', body, headers:{Accept:'application/json'}});
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || c.labels.error);
        document.dispatchEvent(new CustomEvent('cart:update', {bubbles:true, detail:{resource:data, sourceId:button.dataset.vncAdd, data:{source:'product-form-component', itemCount:1, sections:data.sections}}}));
        this.schedule();
      } catch (failure) {
        if (this.isConnected) error.textContent = failure.message || c.labels.error;
      } finally { if (button.isConnected) button.disabled = false; }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  });
})();